import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { map, take } from 'rxjs/operators';
import { ClinicalResolution } from '../models/clinical.model';
import { ClinicalNoteReminderDialogComponent } from '../shared/dialogs/clinical-note-reminder-dialog/clinical-note-reminder-dialog.component';
import { encodeBase64Utf8 } from 'src/app/shared/utils/base64-utf8.utils';

@Injectable({
  providedIn: 'root',
})
export class ClinicalNoteCacheService {
  currentClinicalNoteCached: ClinicalResolution;
  clinicalNoteCacheSubject: BehaviorSubject<ClinicalResolution> = new BehaviorSubject(null);
  clinicalNoteCacheSubject$: Observable<ClinicalResolution> = this.clinicalNoteCacheSubject.asObservable();
  isClinicalNotePendingToSave = false;
  /** Emits HTML content to be written into the Clinical Notes editor (e.g. an AI-generated care call note). */
  setNoteContentSubject = new Subject<string>();
  setNoteContent$: Observable<string> = this.setNoteContentSubject.asObservable();
  /** Holds content pushed while the editor was not yet mounted, so it can be consumed on mount. */
  private pendingNoteContent: string | null = null;
  /** Kind of the pending content — drives the merge policy (see `mergeGeneratedNote`). */
  private pendingNoteKind: 'note' | 'fallback' = 'note';
  /**
   * Bookkeeping for the generated content written into the Clinical Notes editor.
   * `baselineHtml` = what the editor held BEFORE the first insertion; `noteBlock` / `fallbackBlock`
   * = the AI-note block and the captured-responses fallback block, tracked separately so a
   * fallback can never overwrite a full note the user already reviewed; `insertedText` = the
   * plain-text fingerprint of the resulting content. It lives in this singleton service and not
   * in the component so it survives the note panel being closed and reopened.
   */
  private lastGenerated: { baselineHtml: string; noteBlock: string; fallbackBlock: string; insertedText: string } | null = null;
  private isExtraSmall: Observable<BreakpointState> = this.breakpointObserver.observe(Breakpoints.XSmall);

  constructor(private dialog: MatDialog, private readonly breakpointObserver: BreakpointObserver) {}

  /**
   * Pushes content into the Clinical Notes editor. If the editor is already mounted it
   * picks it up via setNoteContent$; otherwise the content is held as pending and consumed
   * by case-note-form on init (when the Notes panel is opened).
   */
  setNoteContent(content: string, kind: 'note' | 'fallback' = 'note'): void {
    this.pendingNoteContent = content;
    this.pendingNoteKind = kind;
    this.setNoteContentSubject.next(content);
  }

  /** Returns and clears any content pushed before the editor was mounted. */
  consumePendingNoteContent(): string | null {
    const content = this.pendingNoteContent;
    this.pendingNoteContent = null;
    return content;
  }

  /** Kind of the content most recently pushed, for the merge policy. */
  pendingNoteContentKind(): 'note' | 'fallback' {
    return this.pendingNoteKind;
  }

  /**
   * Write policy for generated content over the editor's current content:
   * - if the editor still holds exactly what was written last time (compared by TEXT
   *   fingerprint, not HTML: CKEditor normalizes markup on the round-trip), the generated
   *   content is REPLACED, preserving whatever the user had written before it;
   * - if the text changed (the user edited the generated note), the new content is APPENDED, so
   *   text the user typed is never destroyed;
   * - a `fallback` block (the captured responses written when the AI call fails) never overwrites
   *   an intact `note` block: it is appended to it and both are tracked as one unit, so a later
   *   successful generation still replaces the pair instead of stacking a third copy.
   *
   * Known limitation, accepted deliberately: because the fingerprint is text-only, an edit that
   * changes only FORMATTING of the generated block (bolding a word, turning lines into a list)
   * reads as untouched and is lost on the next generation. Comparing markup instead is unusable
   * — CKEditor rewrites the markup we insert on every round-trip.
   *
   * `replaced: false` together with `hadPrevious: true` is the signal to tell the user their
   * edited block was kept and the new content appended below it.
   */
  mergeGeneratedNote(
    currentHtml: string,
    block: string,
    kind: 'note' | 'fallback' = 'note',
  ): { html: string; replaced: boolean; hadPrevious: boolean } {
    const current = currentHtml ?? '';
    const previous = this.lastGenerated;
    const hadPrevious = previous !== null;
    const untouched = previous !== null && this.plainTextFingerprint(current) === previous.insertedText;
    const baseline = untouched ? previous.baselineHtml : current;
    const keptNote = untouched && kind === 'fallback' ? previous.noteBlock : '';
    const noteBlock = kind === 'note' ? block : keptNote;
    const fallbackBlock = kind === 'fallback' ? block : '';
    const generated = `${noteBlock}${fallbackBlock}`;
    const html = baseline ? `${baseline}${generated}` : generated;
    this.lastGenerated = { baselineHtml: baseline, noteBlock, fallbackBlock, insertedText: this.plainTextFingerprint(html) };
    return { html, replaced: untouched, hadPrevious };
  }

  /**
   * Re-syncs the fingerprint with the HTML the editor actually left in the control after
   * normalizing it. Without it the next comparison could fail on normalization alone and degrade
   * to appending.
   *
   * The whole point is the case where the editor changed the TEXT fingerprint, so it cannot be
   * gated on the fingerprint being unchanged — comparing against the html `mergeGeneratedNote`
   * returned made this a no-op by construction (`insertedText` is already that html's
   * fingerprint), leaving the exact risk it documents unmitigated.
   *
   * What it guards against instead is adopting SOMEONE ELSE'S content: the fingerprint is only
   * taken over when the block we wrote is still recognizably present in the control. If another
   * writer (a restored chart draft, localStorage) replaced the content, our block is gone from it,
   * the tracking is left alone, and the next generation appends rather than deleting that content.
   */
  syncGeneratedNoteFingerprint(currentHtml: string): void {
    if (!this.lastGenerated) {
      return;
    }
    const currentFingerprint = this.plainTextFingerprint(currentHtml);
    if (currentFingerprint === this.lastGenerated.insertedText) {
      return;
    }
    const blockFingerprint = this.plainTextFingerprint(
      `${this.lastGenerated.noteBlock}${this.lastGenerated.fallbackBlock}`,
    );
    if (!blockFingerprint || !this.containsBlockText(currentFingerprint, blockFingerprint)) {
      return;
    }
    this.lastGenerated = { ...this.lastGenerated, insertedText: currentFingerprint };
  }

  /** Forgets the tracked generated block (note completed, patient switch, discard). */
  clearGeneratedNoteTracking(): void {
    this.lastGenerated = null;
  }

  saveClinicalNoteInCache(note: ClinicalResolution): void {
    this.isClinicalNotePendingToSave = true;
    this.currentClinicalNoteCached = { ...note };
    if (note.content) {
      localStorage.setItem(`${note.associations?.uid}`, encodeBase64Utf8(note.content));
    }
    this.clinicalNoteCacheSubject.next(note);
  }

  clearClinicalNote(): void {
    localStorage.removeItem(`${this.currentClinicalNoteCached?.associations?.uid}`);
    this.clearObservables();
  }

  clearObservables(): void {
    this.clinicalNoteCacheSubject.next(null);
    this.isClinicalNotePendingToSave = false;
    this.clearGeneratedNoteTracking();
  }

  openClinicalNoteReminderModal(): MatDialogRef<ClinicalNoteReminderDialogComponent> {
    const modal = this.dialog.open(ClinicalNoteReminderDialogComponent, {
      maxWidth: '100vw',
      data: {
        clinicalNote: this.currentClinicalNoteCached,
      },
      disableClose: true,
    });
    this.isExtraSmall.subscribe((size) => {
      if (size.matches) {
        modal.updateSize('100vw', '100vh');
      }
    });
    return modal;
  }

  setclinicalNoteReminderAfterClose(modal: MatDialogRef<ClinicalNoteReminderDialogComponent>, callback?: any): Observable<boolean> {
    return modal.afterClosed().pipe(
      take(1),
      map((resp) => {
        if (resp) {
          return true;
        } else {
          if (callback) {
            callback();
          }
          return false;
        }
      })
    );
  }

  /**
   * Whether `haystack` still contains the generated block's text. Compared with ALL whitespace
   * removed: the editor's normalization is what moved the fingerprint in the first place, and the
   * part of it the `\s+` collapse does not already absorb is whitespace inserted or dropped at
   * block boundaries. Every non-space character of the block must still be present, in order, so
   * content replaced by another writer is not mistaken for our own.
   */
  private containsBlockText(haystack: string, block: string): boolean {
    const squash = (text: string): string => text.replace(/\s/g, '');
    return squash(haystack).includes(squash(block));
  }

  /**
   * Fingerprint that survives CKEditor's markup normalization: text only, whitespace and
   * non-breaking spaces collapsed. Any character the user types changes it.
   *
   * Parsed with DOMParser, NOT `document.createElement('div').innerHTML`: a div belongs to the
   * live document, so parsing into it constructs children in an active document and fires
   * `<img onerror>` / `<svg onload>` and starts `src`/`srcset`/`poster` loads even though the
   * node is never inserted. DOMParser's document is inert, so nothing runs and nothing loads.
   * This matters because `note` is also populated from stored drafts and localStorage, not only
   * from CKEditor-filtered output.
   */
  private plainTextFingerprint(html: string): string {
    if (!html) {
      return '';
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return (parsed.body.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  }
}
